import { GSB_CONFIG, getGsbTenantCode, getGsbToken, getGsbLangCode, normalizeGsbLangCode, } from "../config/gsb-config.js";
import { ClientIds } from "../ids.js";
import { BrowserRegistry } from "../registry.js";
import { ProtocolGuards, } from "../protocol.js";
// Store a singleton instance
let apiServiceInstance = null;
const invalidSessionCodes = new Set(["INVALID_TOKEN", "TOKEN_EXPIRED"]);
export function isInvalidSessionCode(code) {
    return (code !== undefined && invalidSessionCodes.has(code.trim().toUpperCase()));
}
function findHttpErrorPayload(body, parsers, additional) {
    const parse = (value) => {
        const guarded = ProtocolGuards.httpPayload(value);
        const composed = parsers?.payload(value);
        if (!guarded)
            return undefined;
        if (parsers && !composed)
            return undefined;
        return additional ? additional.payload(value) : (composed ?? guarded);
    };
    const direct = parse(body);
    if (direct)
        return direct;
    if (typeof body !== "object" || body === null || !("response" in body)) {
        return undefined;
    }
    return parse(body.response);
}
export class GsbHttpError extends Error {
    static coreParsers;
    static composeProtocolValidation(parsers) {
        GsbHttpError.coreParsers ??= parsers;
    }
    status;
    category;
    code;
    issues;
    details;
    logId;
    systemLogId;
    retryAfterMs;
    body;
    constructor(response, body, options = {}) {
        const registered = BrowserRegistry.lookup("validation")?.http;
        const coreParsers = options.parsers ?? GsbHttpError.coreParsers;
        const payload = findHttpErrorPayload(body, coreParsers ?? registered, coreParsers ? registered : undefined);
        const guardedFallback = ProtocolGuards.httpFallback(body);
        const parsers = coreParsers ?? registered;
        const parsedFallback = parsers?.fallback(body);
        const composedFallback = guardedFallback && (parsers ? parsedFallback : guardedFallback);
        const fallback = composedFallback && coreParsers && registered
            ? registered.fallback(body)
            : composedFallback;
        const authenticationRequired = options.authenticationRequired ?? true;
        const authenticationError = authenticationRequired &&
            (response.status === 401 || isInvalidSessionCode(payload?.code));
        const permissionError = response.status === 403 && !authenticationError;
        super(authenticationError
            ? "Your session has expired. Please sign in again."
            : permissionError
                ? "You don't have permission to perform this action."
                : (payload?.message ??
                    fallback?.message ??
                    `HTTP request failed with status ${response.status}`), { cause: response });
        this.name = "GsbHttpError";
        this.status = response.status;
        this.category = authenticationError
            ? "authentication"
            : permissionError
                ? "permission"
                : "request";
        this.code = payload?.code;
        this.issues = payload?.issues ?? [];
        this.details = payload?.details;
        this.logId = fallback?.logId;
        this.systemLogId = fallback?.systemLogId;
        const retryAfter = response.headers.get("Retry-After")?.trim();
        if (retryAfter) {
            const delay = /^\d+$/.test(retryAfter)
                ? Number(retryAfter) * 1000
                : Date.parse(retryAfter) - Date.now();
            if (Number.isFinite(delay) && delay >= 0)
                this.retryAfterMs = delay;
        }
        this.body = body;
    }
}
function parseResponseBody(responseText) {
    if (!responseText.trim())
        return null;
    try {
        const value = JSON.parse(responseText);
        return value;
    }
    catch {
        return responseText;
    }
}
function isMutableRequestContent(content) {
    return typeof content === "object" && content !== null;
}
function getRequestId(content) {
    return isMutableRequestContent(content) && typeof content.id === "string"
        ? content.id
        : undefined;
}
function getBusinessErrorMessage(value) {
    if (typeof value !== "object" || value === null)
        return undefined;
    const message = "message" in value && typeof value.message === "string"
        ? value.message
        : undefined;
    const status = "status" in value && typeof value.status === "number"
        ? value.status
        : undefined;
    const validationErrors = "validationErrors" in value &&
        Array.isArray(value.validationErrors) &&
        value.validationErrors.every((issue) => typeof issue === "string")
        ? value.validationErrors.filter((issue) => issue.trim().length > 0)
        : [];
    const failureMessage = validationErrors.length
        ? validationErrors.join("\n")
        : message;
    if ("success" in value && value.success === false) {
        return failureMessage ?? "Bulk operation failed";
    }
    if (status !== undefined && status >= 400) {
        return failureMessage ?? `Bulk operation failed with status ${status}`;
    }
    if (message?.toLowerCase().includes("system error"))
        return message;
    return undefined;
}
export class GsbApiService {
    // We'll use a simpler structure with only one base URL that changes based on tenant
    pendingBulkCalls = new Map();
    bulkCallCounter = 0;
    requestValidator = null;
    unauthorizedHandler = null;
    protocolParsers;
    constructor() { }
    composeProtocolValidation(parsers) {
        this.protocolParsers = parsers;
    }
    /**
     * Install (or clear with `null`) a pre-flight validator for every outgoing request.
     * Without one, bodies go to the server unchecked — the historical behaviour.
     */
    setRequestValidator(validator) {
        this.requestValidator = validator;
    }
    hasRequestValidator() {
        return this.requestValidator !== null;
    }
    setUnauthorizedHandler(handler) {
        this.unauthorizedHandler = handler;
    }
    /**
     * Get the singleton instance of the service
     */
    static getInstance() {
        if (typeof window !== "undefined") {
            return BrowserRegistry.acquire("api", () => new this());
        }
        if (!apiServiceInstance) {
            apiServiceInstance = new this();
        }
        return apiServiceInstance;
    }
    getBaseUrl(tenantCode, baseDomain, apiBaseUrl) {
        if (apiBaseUrl)
            return apiBaseUrl;
        return GSB_CONFIG.API_URL(tenantCode, baseDomain);
    }
    /**
     * Extract tenant code from a JWT token
     * @param token The JWT token
     * @returns The tenant code or undefined
     */
    extractTenantCode(token) {
        return GSB_CONFIG.extractTenantCode(token);
    }
    /**
     * Generate a unique request ID for bulk operations
     */
    generateReqId() {
        return `req_${Date.now()}_${++this.bulkCallCounter}`;
    }
    /**
     * Get the bulk batch key for grouping requests
     */
    getBulkBatchKey(tenantCode, token) {
        return `${tenantCode || "default"}_${token || "noauth"}`;
    }
    /**
     * Ensure request has a unique ID for bulk operations
     */
    ensureRequestId(request) {
        if (isMutableRequestContent(request.content)) {
            // Check if the request content doesn't have an id
            if (!request.content.id) {
                request.content.id = ClientIds.newId();
            }
        }
    }
    /**
     * Execute a bulk batch
     */
    async executeBulkBatch(batchKey) {
        const batch = this.pendingBulkCalls.get(batchKey);
        if (!batch || batch.calls.length === 0) {
            return;
        }
        // Remove the batch from pending calls
        this.pendingBulkCalls.delete(batchKey);
        try {
            // Prepare bulk request - use request.id as reqId for proper response matching
            const bulkCalls = batch.calls.map((call) => ({
                endPoint: call.endPoint,
                request: call.request.content,
                reqId: getRequestId(call.request.content) || call.reqId,
            }));
            const bulkRequest = {
                bulkCalls,
                variation: {
                    tenantCode: batch.tenantCode || "default",
                    langCode: normalizeGsbLangCode(getGsbLangCode()),
                },
            };
            // Execute bulk request
            const bulkResponse = await this.httpCallAsync({
                method: "POST",
                protocol: "https",
                hostName: "api",
                content: bulkRequest,
                path: "/api/entity/bulk",
                bearerToken: batch.token,
                contentType: "application/json",
                tenantCode: batch.tenantCode,
            });
            // Process individual responses
            if (!ProtocolGuards.bulk(bulkResponse))
                throw new Error("Bulk operation failed: No results returned");
            const bulkResult = bulkResponse;
            if (bulkResult.results) {
                // Create a map for quick lookup using reqId (which should match request.id)
                const resultMap = new Map();
                for (const result of bulkResult.results) {
                    if (result.reqId) {
                        if (resultMap.has(result.reqId))
                            throw new Error("Bulk operation failed: Duplicate result ID");
                        const parsed = ProtocolGuards.bulkChild(result.response);
                        const childError = getBusinessErrorMessage(result);
                        resultMap.set(result.reqId, childError
                            ? new Error(childError)
                            : (parsed ??
                                new Error("Bulk operation failed: Invalid child response")));
                    }
                }
                // Resolve individual promises using the request.id to match responses
                for (const call of batch.calls) {
                    const lookupId = getRequestId(call.request.content) || call.reqId;
                    const result = resultMap.get(lookupId);
                    if (result instanceof Error) {
                        call.reject(result);
                    }
                    else if (result !== undefined) {
                        const businessError = getBusinessErrorMessage(result);
                        if (businessError) {
                            call.reject(new Error(businessError));
                        }
                        else {
                            call.resolve(result);
                        }
                    }
                    else {
                        call.reject(new Error(`No result found for request ID ${lookupId}`));
                    }
                }
            }
            else {
                // If no results, reject all calls
                for (const call of batch.calls) {
                    call.reject(new Error("Bulk operation failed: No results returned"));
                }
            }
        }
        catch (error) {
            // If bulk operation fails, reject all individual calls
            for (const call of batch.calls) {
                call.reject(error);
            }
        }
    }
    /**
     * Add a call to the bulk batch
     */
    addToBulkBatch(request, endPoint, token, tenantCode) {
        return new Promise((resolve, reject) => {
            // Ensure the request has a unique ID for tracking
            this.ensureRequestId(request);
            // Use the request.id as the reqId for proper response matching
            const reqId = getRequestId(request.content) || this.generateReqId();
            const batchKey = this.getBulkBatchKey(tenantCode, token);
            const pendingCall = {
                request,
                endPoint,
                token,
                tenantCode,
                resolve,
                reject,
                reqId,
            };
            // Get or create batch
            let batch = this.pendingBulkCalls.get(batchKey);
            if (!batch) {
                batch = {
                    calls: [],
                    timer: setTimeout(() => {
                        void this.executeBulkBatch(batchKey);
                    }, 50), // 50ms delay
                    tenantCode,
                    token,
                };
                this.pendingBulkCalls.set(batchKey, batch);
            }
            else {
                // Clear existing timer and set a new one
                clearTimeout(batch.timer);
                batch.timer = setTimeout(() => {
                    void this.executeBulkBatch(batchKey);
                }, 50);
            }
            // Add call to batch
            batch.calls.push(pendingCall);
        });
    }
    /**
     * Make an HTTP call
     */
    async httpCall(dto, callback, errorCallback) {
        try {
            const response = await this.httpCallAsync(dto);
            if (callback) {
                callback(response);
            }
            return response;
        }
        catch (error) {
            if (errorCallback) {
                errorCallback(error);
            }
            throw error;
        }
    }
    /**
     * Execute HTTP call asynchronously
     */
    async httpCallAsync(dto) {
        const requestData = this.convertToRequestData(dto);
        const response = await fetch(requestData.url, requestData.init);
        const responseText = await response.text();
        const responseBody = parseResponseBody(responseText);
        if (!response.ok) {
            if (response.status === 401 && !dto.noAuth) {
                this.unauthorizedHandler?.();
            }
            throw new GsbHttpError(response, responseBody, {
                authenticationRequired: !dto.noAuth,
                parsers: this.protocolParsers,
            });
        }
        if (responseBody === null)
            return null;
        if (dto.jsonResponse === false)
            return responseText;
        if (typeof responseBody === "string") {
            throw new Error(`Expected a JSON response but received invalid JSON (HTTP ${response.status}).`);
        }
        return responseBody;
    }
    /**
     * Convert request parameters to fetch request data
     */
    convertToRequestData(requestParameters) {
        const url = new URL(requestParameters.path || "", this.getBaseUrl(requestParameters.tenantCode, requestParameters.baseDomain, requestParameters.apiBaseUrl)).toString();
        const init = {
            method: requestParameters.method,
            headers: {
                // Only add Authorization header if noAuth is not true and we have a bearerToken
                ...(!requestParameters.noAuth && requestParameters.bearerToken
                    ? { Authorization: `Bearer ${requestParameters.bearerToken}` }
                    : {}),
                ...requestParameters.headers,
            },
        };
        if (!requestParameters.content)
            requestParameters.content = {};
        // Handle FormData requests
        if (requestParameters.isFormData) {
            const formData = new FormData();
            // Convert JSON content to FormData
            const entries = isMutableRequestContent(requestParameters.content)
                ? Object.entries(requestParameters.content)
                : [];
            for (const [key, value] of entries) {
                if (value instanceof File) {
                    formData.append(key, value, value.name);
                }
                else if (value instanceof Blob) {
                    formData.append(key, value);
                }
                else if (typeof value === "object" && value !== null) {
                    // Handle objects by JSON stringifying them
                    formData.append(key, JSON.stringify(value));
                }
                else if (value !== undefined && value !== null) {
                    // Handle primitive values
                    formData.append(key, String(value));
                }
            }
            init.body = formData;
            // Don't set Content-Type for FormData - let the browser set it with boundary
        }
        else {
            // Handle regular JSON requests
            init.headers = {
                ...init.headers,
                "Content-Type": requestParameters.contentType || "application/json",
            };
            if (requestParameters.content) {
                init.body = JSON.stringify(requestParameters.content);
            }
        }
        return { url, init };
    }
    /**
     * Call the API with the appropriate token and tenant code
     */
    async callApi(req, endPoint, token, tenantCode) {
        // Skip token logic if noAuth is true
        let bearerToken = undefined;
        if (!req.noAuth) {
            // If no token is provided, get the appropriate token based on the endpoint
            if (!token) {
                token = getGsbToken() || undefined;
            }
            bearerToken = token;
        }
        // If no tenant code is provided and we have a token, extract it from the token
        if (!tenantCode && token) {
            tenantCode = this.extractTenantCode(token);
        }
        // If still no tenant code, get it from config
        if (!tenantCode) {
            tenantCode = getGsbTenantCode();
        }
        if (tenantCode && isMutableRequestContent(req.content)) {
            if (!req.content.variation) {
                req.content.variation = {};
            }
            req.content.variation.tenantCode ??= tenantCode;
            req.content.variation.langCode = normalizeGsbLangCode(req.content.variation.langCode ?? getGsbLangCode());
        }
        // Prepare the request
        const request = {
            ...req,
            path: endPoint,
            bearerToken: bearerToken,
            contentType: req.contentType || "application/json",
            jsonResponse: req.jsonResponse !== undefined ? req.jsonResponse : true,
            tenantCode: tenantCode,
        };
        // Validate after variation is stamped, so the body is exactly what will be sent.
        this.requestValidator?.({ endpoint: endPoint, body: req.content });
        // Check if we should use bulk operations
        const useBulk = req.useBulk !== false;
        if (useBulk) {
            // Add to bulk batch
            return this.addToBulkBatch(request, endPoint, bearerToken, tenantCode);
        }
        else {
            // Execute immediately
            return this.httpCall(request);
        }
    }
}
//# sourceMappingURL=gsb-api.service.js.map